import { useState } from 'react';
import { toast } from 'react-toastify';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { PageContentLayout } from '@tassomutuo-backoffice/ui';
import { BankInfo, PostBankInfo } from '@tassomutuo-backoffice/types';
import BankInfosTable from './components/BankInfosTable';
import ModalBankInfoForm from './components/ModalBankInfoForm';
import {
  updateBankInfo,
  createBankInfo,
  deleteBankInfo,
} from '../../../services/api/bank-info/bank-info';

const BankInfosListPage = () => {
  const queryClient = useQueryClient();
  const [selectedBankInfo, setSelectedBankInfo] = useState<BankInfo | null>(
    null
  );
  const [showModalForm, setShowModalForm] = useState(false);

  const onEditRowClick = (bankInfo: BankInfo) => {
    setSelectedBankInfo(bankInfo);
  };

  const updateBankInfoMutation = useMutation<
    BankInfo,
    Error,
    { id: string; data: PostBankInfo }
  >({
    mutationFn: ({ id, data }) => updateBankInfo(id, data),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: ['BankInfos'],
      });
      toast.success('Info bancarie aggiornato!');
    },
    onError: (error) => {
      toast.error(error.message || 'Si è verificato un errore, riprova!');
    },
  });

  const createBankInfoMutation = useMutation({
    mutationFn: createBankInfo,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: ['BankInfos'],
      });
      toast.success('Info bancarie creata!');
    },
    onError: (error) => {
      toast.error(error.message || 'Si è verificato un errore, riprova!');
    },
  });

  const deleteBankInfoMutation = useMutation({
    mutationFn: deleteBankInfo,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: ['BankInfos'],
      });
      toast.success('Info bancarie cancellato!');
    },
    onError: (error) => {
      toast.error(error.message || 'Si è verificato un errore, riprova!');
    },
  });

  const closeModalForm = () => {
    setSelectedBankInfo(null);
    setShowModalForm(false);
  };

  return (
    <>
      <PageContentLayout title="Informazioni Bancarie" error={null}>
        <BankInfosTable
          onEditRowClick={onEditRowClick}
          onCreateNewClick={() => setShowModalForm(true)}
          onDeleteRowClick={async (bankInfoId) =>
            deleteBankInfoMutation.mutateAsync(bankInfoId)
          }
        />
      </PageContentLayout>
      <ModalBankInfoForm
        open={!!selectedBankInfo || showModalForm}
        bankInfoToUpdate={selectedBankInfo}
        handleClose={closeModalForm}
        onSubmitBankInfoToUpdate={async ({ bankInfoId, ...data }) => {
          await updateBankInfoMutation.mutateAsync({
            id: bankInfoId,
            data: data as PostBankInfo,
          });
        }}
        onSubmitBankInfoToCreate={async (data) => {
          await createBankInfoMutation.mutateAsync(data as PostBankInfo);
        }}
      />
    </>
  );
};

export default BankInfosListPage;
